import * as Yup from "yup";
import "yup-phone-lite";
import {
  formatToNaira,
  parseDateStringToDate,
  parseNairaToNumber,
  parseTimeToDate,
} from "./helpers";

export const SignInSchema = Yup.object({
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  password: Yup.string().required("Password is required"),
});

export const SignUpSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
});

export const VerifySignUpSchema = Yup.object({
  code: Yup.string().required("Code is required"),
});

export const ForgotPasswordSchema = Yup.object({
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
});

export const ResetPasswordSchema = Yup.object({
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .required("You must confirm your password")
    .oneOf([Yup.ref("password")], "Passwords do not match"),
});

export const EventDetailsSchema = Yup.object({
  name: Yup.string().required("Event name is required"),
  description: Yup.string().required("Description is rquired"),
  location: Yup.string().required("Required"),
  startDate: Yup.string().required("Start date is required"),
  endDate: Yup.string()
    .required("End date is required")
    .test(
      "is-greater",
      "End date must be later than start date",
      function (value) {
        const { startDate } = this.parent;
        return !startDate || !value || new Date(value) >= new Date(startDate);
      },
    ),
  startTime: Yup.string().required("Start time is required"),
  endTime: Yup.string()
    .required("End time is required")
    .test(
      "is-greater",
      "End time must be later than start time",
      function (value) {
        const { startTime, startDate, endDate } = this.parent;

        if (
          parseTimeToDate(value) !== null &&
          parseTimeToDate(startTime) !== null &&
          parseDateStringToDate(startDate) !== null &&
          parseDateStringToDate(endDate) !== null
        ) {
          return (
            (startTime &&
              value &&
              parseTimeToDate(value)! > parseTimeToDate(startTime)! &&
              endDate! === startDate!) ||
            (startTime &&
              value &&
              parseDateStringToDate(endDate)! >
                parseDateStringToDate(startDate)!)
          );
        } else {
          return false;
        }
      },
    ),
  image: Yup.string(),
  type: Yup.string().required("Event type is required"),
  status: Yup.string().required("Event status is required"),
});

export const EventTicketSchema = Yup.object({
  type: Yup.string().required("Ticket type is required"),
  name: Yup.string().required("Ticket name is required"),
  description: Yup.string(),
  price: Yup.string().when("type", ([type], schema) => {
    return type === "paid"
      ? schema
          .required("Price is required")
          .test("is-valid-amount", "Invalid amount", function (value) {
            return parseNairaToNumber(value) !== null;
          })
          .test(
            "is-greater-than-zero",
            `Ticket price should be at least ${formatToNaira(1000)}`,
            function (value) {
              return (
                parseNairaToNumber(value) !== null &&
                parseNairaToNumber(value)! >= 1000
              );
            },
          )
      : schema.notRequired();
  }),
  quantityType: Yup.string().required("Quantity type is required"),
  quantity: Yup.string().when("quantityType", ([quantityType], schema) => {
    return quantityType === "limited"
      ? schema
          .required("Quantity is required")
          .test("is-valid-amount", "Invalid quantity", function (value) {
            return parseNairaToNumber(value) !== null;
          })
          .test(
            "is-greater-than-zero",
            "Ticket quantity must be greater than 0",
            function (value) {
              return (
                parseNairaToNumber(value) !== null &&
                parseNairaToNumber(value)! > 0
              );
            },
          )
      : schema.notRequired();
  }),
});

export const PurchaseTicketSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  hasGuests: Yup.boolean().required(),
  guests: Yup.array()
    .when("hasGuests", (hasGuests, schema) => {
      return hasGuests[0]
        ? schema.of(
            Yup.object().shape({
              firstName: Yup.string().required("First name is required"),
              lastName: Yup.string().required("Last name is required"),
              email: Yup.string()
                .email("Email is not a valid email")
                .required("Email is required"),
              title: Yup.string(),
              ticketId: Yup.string(),
            }),
          )
        : schema.of(
            Yup.object().shape({
              firstName: Yup.string(),
              lastName: Yup.string(),
              email: Yup.string(),
              title: Yup.string(),
              ticketId: Yup.string(),
            }),
          );
    })

    .min(0),
});

export const AddPayoutSchema = Yup.object({
  bankName: Yup.string().required("Bank name is required"),
  accountNumber: Yup.string().required("Account number is required"),
  bankCode: Yup.string().required("Bank code is required"),
});

export const UpdateProfileSchema = Yup.object({
  firstName: Yup.string(),
  lastName: Yup.string(),
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
});

export const SettingPageUpdateProfileSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
});

export const DiscountSchema = Yup.object({
  name: Yup.string().required("Discount name is required"),
  type: Yup.string().required("Discount type is required"),
  value: Yup.string().when("type", ([type], schema) => {
    if (type === "amount") {
      return schema
        .required(`${type} is required`)
        .test("is-valid-amount", `Invalid ${type}`, function (value) {
          return parseNairaToNumber(value) !== null;
        })
        .test(
          "is-greater-than-zero",
          `${type} must be greater than 0`,
          function (value) {
            return (
              parseNairaToNumber(value) !== null &&
              parseNairaToNumber(value)! > 0
            );
          },
        );
    } else if (type === "percentage") {
      return schema
        .required(`${type} is required`)
        .test("is-valid-amount", `Invalid ${type}`, function (value) {
          return parseNairaToNumber(value) !== null;
        })
        .test(
          "is-greater-than-zero",
          `${type} must be greater than 0`,
          function (value) {
            return (
              parseNairaToNumber(value) !== null &&
              parseNairaToNumber(value)! > 0
            );
          },
        )
        .test(
          "is-not-more-than-hundred",
          `${type} cannot exceed 100%`,
          function (value) {
            return (
              parseNairaToNumber(value) !== null &&
              parseNairaToNumber(value)! <= 100
            );
          },
        );
    } else {
      return schema.notRequired();
    }
  }),
  quantityType: Yup.string().required("Quantity type is required"),
  quantity: Yup.string().when("quantityType", ([quantityType], schema) => {
    return quantityType === "limited"
      ? schema
          .required("Quantity is required")
          .test("is-valid-amount", "Invalid quantity", function (value) {
            return parseNairaToNumber(value) !== null;
          })
          .test(
            "is-greater-than-zero",
            "Quantity must be greater than 0",
            function (value) {
              return (
                parseNairaToNumber(value) !== null &&
                parseNairaToNumber(value)! > 0
              );
            },
          )
      : schema.notRequired();
  }),
});

export const OrganisationInformationSchema = Yup.object({
  isOrganisation: Yup.boolean().required(),
  phoneNumber: Yup.string()
    .phone("NG", "Enter a valid Nigerian number")
    .optional(),
  // phoneNumber: Yup.string()
  // .required("Phone number is required")
  // .matches(/^[+]?[0-9]{10,15}$/, "Phone number must be a valid number"),
  name: Yup.string().required("Organization name is required"),
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  logo: Yup.string().optional(),
});
